import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Popover } from '@c2fo/liquidity';
import invoices from '@/enterprise/assets/invoicesgrey.svg?url';
import EmptyRender from '@/enterprise/components/EmptyRender';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableCell,
  TableDisplay,
  TableHead,
  TableHeader,
  TableRow,
  TableSortArrow,
} from '@/enterprise/components/Table';
import useAdjustmentInvoices from '@/enterprise/data/useAdjustmentInvoices';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { AdjustmentInvoiceSortKey, AdjustmentInvoiceSource, AdjustmentInvoiceUrlState } from './types';
import { adjustmentTypeConfig, mapUrlStateToApiSearchFilters } from './utils';

export const AdjustmentsTable = ({
  currentCursor,
  setNextCursor,
  urlState,
  handleSort,
  source,
}: {
  currentCursor: string | null;
  setNextCursor: (cursor: string | null) => void;
  urlState: AdjustmentInvoiceUrlState;
  handleSort: (sortKey: AdjustmentInvoiceSortKey) => void;
  source: AdjustmentInvoiceSource;
}) => {
  const { t } = useTranslation();
  const { asDateStringText, asCurrency } = useLocaleFormat();
  const { makerOrganizationUuid, takerMarkets } = source;

  const search = useMemo(() => mapUrlStateToApiSearchFilters({ urlState }), [urlState]);
  const { sort } = urlState;

  const { data } = useAdjustmentInvoices({
    makerOrganizationUuid,
    takerMarkets,
    options: {
      sortBy: sort.key,
      sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
      ...(currentCursor && { cursor: currentCursor }),
      limit: urlState.pageSize,
    },
    ...(!!search && {
      search,
    }),
  });

  const nextCursor = data?.pageInfo?.nextCursor ?? null;

  useEffect(() => {
    setNextCursor(nextCursor);
  }, [nextCursor, setNextCursor]);

  const adjustmentInvoices = useMemo(() => data?.invoices ?? [], [data?.invoices]);

  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead isSorted={sort.key === 'voucherId'} onClick={() => handleSort('voucherId')}>
              <TableSortArrow accessorKey="voucherId" sort={sort}>
                {t('core.invoiceId')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'amount'} onClick={() => handleSort('amount')}>
              <TableSortArrow accessorKey="amount" sort={sort} textRight>
                {t('core.adjustmentAmount')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'originalDepositDate'} onClick={() => handleSort('originalDepositDate')}>
              <TableSortArrow accessorKey="originalDepositDate" sort={sort}>
                {t('core.maturityDate')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'adjustmentInvoiceId'} onClick={() => handleSort('adjustmentInvoiceId')}>
              <TableSortArrow accessorKey="adjustmentInvoiceId" sort={sort}>
                {t('maker.adjustmentId')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'status'} onClick={() => handleSort('status')}>
              <TableSortArrow accessorKey="status" sort={sort} textRight>
                {t('core.adjustmentType')}
              </TableSortArrow>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {adjustmentInvoices.length > 0 ? (
            adjustmentInvoices.map((adjustmentInvoice) => {
              return (
                <TableRow key={adjustmentInvoice.voucherId}>
                  {/* Invoice Id */}
                  <TableCell>
                    <div className="max-w-80 whitespace-pre-wrap break-words">{adjustmentInvoice.voucherId}</div>
                  </TableCell>
                  {/* Adjustment Amount */}
                  <TableCell className="text-right">
                    {asCurrency(adjustmentInvoice.amount, adjustmentInvoice.currency)}
                  </TableCell>
                  {/* Maturity Date */}
                  <TableCell>
                    {adjustmentInvoice.originalDepositDate &&
                      asDateStringText({ date: adjustmentInvoice.originalDepositDate })}
                  </TableCell>
                  {/* Adjustment ID */}
                  <TableCell>
                    <EmptyRender condition={!!adjustmentInvoice.adjustmentInvoiceId}>
                      {adjustmentInvoice.adjustmentInvoiceId}
                    </EmptyRender>
                  </TableCell>
                  {/* Adjustment Type */}
                  <TableCell className="flex justify-end">
                    <Popover>
                      <Popover.Trigger aria-label="Adjustment type information">
                        <Chip
                          label={t(adjustmentTypeConfig[adjustmentInvoice.status].labelKey)}
                          type={adjustmentTypeConfig[adjustmentInvoice.status].chipType}
                          useHoverStyles
                        />
                      </Popover.Trigger>
                      <Popover.Content arrow collisionPadding={24}>
                        <div className="w-72 space-y-2 text-center text-sm">
                          <div>{t(adjustmentTypeConfig[adjustmentInvoice.status].tooltipKey)}</div>
                        </div>
                      </Popover.Content>
                    </Popover>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableDisplay colSpan={5}>
              <NoDataState
                className="py-8"
                icon={<img src={invoices} alt="emptystatements" className="h-28 w-28" />}
                title={t('invoices.noAvailableInvoices')}
                message={t('invoices.noAvailableEligibleInvoicesDescription')}
              />
            </TableDisplay>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default AdjustmentsTable;
